import React, { useState, useEffect, useCallback } from "react";
import { getLeadPriceCalendar } from "../../services/api";
import { FaChevronLeft, FaChevronRight, FaPlane } from "react-icons/fa";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LeadPriceFare {
    departureDate: string;
    returnDate?: string;
    lowestFare: number;
    lowestNonStopFare?: number;
    currencyCode: string;
    airline?: string;
}

export interface FareCalendarProps {
    origin: string;
    destination: string;
    initialDate?: string;
    onDateSelect?: (date: string, fare: number, currency: string) => void;
    tripType?: "oneway" | "roundtrip";
    lengthOfStay?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function startOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function daysInMonth(y: number, m: number): number {
    return new Date(y, m + 1, 0).getDate();
}
function fmtYMD(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtMonthYear(d: Date): string {
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
function isToday(y: number, m: number, d: number): boolean {
    const t = new Date();
    return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
}
function isPast(y: number, m: number, d: number): boolean {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return new Date(y, m, d) < t;
}

function formatFare(amount: number, currency: string): string {
    if (amount >= 100000) return `${currency} ${(amount / 1000).toFixed(0)}K`;
    if (amount >= 10000) return `${currency} ${(amount / 1000).toFixed(1)}K`;
    return `${currency} ${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/** Classify fare into quartile: 0=cheapest, 1=low, 2=medium, 3=high */
function classifyFare(fare: number, min: number, max: number): number {
    if (max === min) return 0;
    const pct = (fare - min) / (max - min);
    if (pct <= 0.25) return 0;
    if (pct <= 0.5) return 1;
    if (pct <= 0.75) return 2;
    return 3;
}

const FARE_COLORS = [
    "bg-emerald-50 text-emerald-700 border-emerald-200",
    "bg-sky-50 text-sky-700 border-sky-200",
    "bg-amber-50 text-amber-700 border-amber-200",
    "bg-red-50 text-red-700 border-red-200",
];
const FARE_LEGEND = [
    { label: "Cheapest", cls: "bg-emerald-400" },
    { label: "Low", cls: "bg-sky-400" },
    { label: "Medium", cls: "bg-amber-400" },
    { label: "High", cls: "bg-red-400" },
];

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FareCalendar: React.FC<FareCalendarProps> = ({
    origin,
    destination,
    initialDate,
    onDateSelect,
    tripType = "oneway",
    lengthOfStay,
}) => {
    const init = initialDate ? new Date(initialDate + "T00:00:00") : new Date();
    const [viewMonth, setViewMonth] = useState(startOfMonth(init));
    const [fares, setFares] = useState<Map<string, LeadPriceFare>>(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const fetchFares = useCallback(async () => {
        if (!origin || !destination) return;
        setLoading(true);
        setError(null);
        try {
            const depDate = fmtYMD(viewMonth);
            const result = await getLeadPriceCalendar({
                origin,
                destination,
                departureDate: depDate,
                ...(tripType === "roundtrip" && lengthOfStay ? { lengthOfStay } : {}),
            });
            if (result?.success && Array.isArray(result.fares)) {
                const map = new Map<string, LeadPriceFare>();
                for (const f of result.fares) {
                    const key = (f.departureDate || "").slice(0, 10);
                    if (key) map.set(key, f);
                }
                setFares(map);
            } else {
                setError(result?.error || "No fare data available for this route.");
                setFares(new Map());
            }
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.message || "Failed to load fare calendar.");
            setFares(new Map());
        } finally {
            setLoading(false);
        }
    }, [origin, destination, viewMonth, tripType, lengthOfStay]);

    useEffect(() => {
        fetchFares();
    }, [fetchFares]);

    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth();
    const numDays = daysInMonth(y, m);
    const firstDow = (new Date(y, m, 1).getDay() + 6) % 7; // Mon=0

    const prevMonth = () => setViewMonth(new Date(y, m - 1, 1));
    const nextMonth = () => setViewMonth(new Date(y, m + 1, 1));

    // Build array of day cells (including empty leading cells)
    const cells: Array<{ day: number | null; fare?: LeadPriceFare }> = [];
    for (let i = 0; i < firstDow; i++) cells.push({ day: null });
    for (let d = 1; d <= numDays; d++) {
        const key = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        cells.push({ day: d, fare: fares.get(key) });
    }

    // Price range for coloring
    const fareValues = Array.from(fares.values()).map((f) => f.lowestFare).filter((v) => v > 0);
    const minFare = fareValues.length > 0 ? Math.min(...fareValues) : 0;
    const maxFare = fareValues.length > 0 ? Math.max(...fareValues) : 0;

    const handleDayClick = (day: number, fare?: LeadPriceFare) => {
        if (isPast(y, m, day)) return;
        const key = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        setSelectedDate(key);
        if (onDateSelect && fare) {
            onDateSelect(key, fare.lowestFare, fare.currencyCode);
        } else if (onDateSelect) {
            onDateSelect(key, 0, "");
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <button
                        onClick={prevMonth}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-500"
                    >
                        <FaChevronLeft size={12} />
                    </button>
                    <h3 className="text-sm font-bold text-gray-900 min-w-[140px] text-center">
                        {fmtMonthYear(viewMonth)}
                    </h3>
                    <button
                        onClick={nextMonth}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-500"
                    >
                        <FaChevronRight size={12} />
                    </button>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                    <span className="font-bold text-gray-700">{origin}</span>
                    <FaPlane size={10} className="text-primary" />
                    <span className="font-bold text-gray-700">{destination}</span>
                </div>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-gray-100">
                {DAY_LABELS.map((d) => (
                    <div key={d} className="py-2 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {d}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            {loading ? (
                <div className="grid grid-cols-7 gap-px bg-gray-100 p-px">
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div key={i} className="bg-white h-16 sm:h-20 animate-pulse">
                            <div className="m-2 h-3 w-6 bg-gray-200 rounded" />
                            <div className="mx-2 mt-1 h-3 w-12 bg-gray-100 rounded" />
                        </div>
                    ))}
                </div>
            ) : error ? (
                <div className="py-12 text-center">
                    <p className="text-sm text-gray-500 mb-2">{error}</p>
                    <button
                        onClick={fetchFares}
                        className="text-sm text-primary font-semibold hover:underline"
                    >
                        Retry
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-7 gap-px bg-gray-100 p-px">
                    {cells.map((cell, idx) => {
                        if (cell.day === null) {
                            return <div key={`e-${idx}`} className="bg-gray-50 h-16 sm:h-20" />;
                        }
                        const past = isPast(y, m, cell.day);
                        const today = isToday(y, m, cell.day);
                        const key = `${y}-${String(m + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
                        const isSelected = selectedDate === key;
                        const fare = cell.fare;
                        const quartile = fare ? classifyFare(fare.lowestFare, minFare, maxFare) : -1;

                        return (
                            <button
                                key={key}
                                disabled={past}
                                onClick={() => handleDayClick(cell.day!, fare)}
                                className={`relative h-16 sm:h-20 text-left px-1.5 sm:px-2 py-1 transition-colors ${
                                    past
                                        ? "bg-gray-50 text-gray-300 cursor-default"
                                        : isSelected
                                        ? "bg-primary/10 ring-2 ring-primary ring-inset"
                                        : fare
                                        ? `${FARE_COLORS[quartile]} hover:brightness-95 cursor-pointer`
                                        : "bg-white hover:bg-gray-50 cursor-pointer"
                                }`}
                            >
                                <span className={`text-xs font-medium ${
                                    today ? "bg-primary text-white w-5 h-5 rounded-full inline-flex items-center justify-center" : ""
                                }`}>
                                    {cell.day}
                                </span>
                                {fare && !past && (
                                    <div className="mt-0.5">
                                        <div className="text-[10px] sm:text-xs font-bold leading-tight truncate">
                                            {formatFare(fare.lowestFare, fare.currencyCode)}
                                        </div>
                                        {fare.airline && (
                                            <div className="text-[8px] sm:text-[10px] opacity-60 truncate">
                                                {fare.airline}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!fare && !past && (
                                    <div className="text-[9px] text-gray-300 mt-1">—</div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Legend + info */}
            <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    {FARE_LEGEND.map((l) => (
                        <div key={l.label} className="flex items-center gap-1">
                            <div className={`w-2.5 h-2.5 rounded-sm ${l.cls}`} />
                            <span className="text-[10px] text-gray-500">{l.label}</span>
                        </div>
                    ))}
                </div>
                <span className="text-[10px] text-gray-400">Prices are indicative and may vary</span>
            </div>
        </div>
    );
};

export default FareCalendar;
